/**
 * Hersteller-Bestellcode-Katalog — UI-Bausteine (Jun 2026, User-Wunsch).
 *  - ManufacturerCatalogImport: Button + Dialog zum Importieren/Aktualisieren
 *    des Katalogs (Datei wie materialcode.txt).
 *  - CatalogOrderCodePicker: Hersteller wählen → Bestellcode/Modell suchen &
 *    übernehmen (befüllt Marke, Bestellcode, Modell D.3).
 */
import { useState, useEffect, useCallback } from "react";
import { Upload, Loader2, CheckCircle2, PackageSearch, Undo2, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import api from "../../lib/api";
import { toast } from "sonner";

export function ManufacturerCatalogImport({ onImported, hideTrigger = false, externalOpen, onExternalOpenChange }) {
    const [openInternal, setOpenInternal] = useState(false);
    // Jul-2026 (User-Wunsch) — Dialog auch aus dem „Weitere Funktionen"-Menü steuerbar.
    const open = externalOpen !== undefined ? externalOpen : openInternal;
    const setOpen = onExternalOpenChange || setOpenInternal;
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [reverted, setReverted] = useState({}); // {listingId: true}
    // Jul-2026 (User-Wunsch) — Auto-Import: hinterlegte Hersteller-Datei/URL wird
    // jeden Montag automatisch importiert (statt nur Erinnerung).
    const [auto, setAuto] = useState(null); // {enabled, source_url, has_file, filename, last_run_at, last_result}
    const [autoUrl, setAutoUrl] = useState("");
    const [autoBusy, setAutoBusy] = useState(false);
    const loadAuto = useCallback(async () => {
        try {
            const { data } = await api.get("/dealer/manufacturer-catalog/auto-import");
            setAuto(data || {});
            setAutoUrl(data?.source_url || "");
        } catch { setAuto({}); }
    }, []);
    useEffect(() => { if (open) loadAuto(); }, [open, loadAuto]);
    const saveAuto = async (patch) => {
        setAutoBusy(true);
        try {
            await api.put("/dealer/manufacturer-catalog/auto-import", patch);
            await loadAuto();
            toast.success("Auto-Import-Einstellungen gespeichert");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setAutoBusy(false); }
    };
    const storeFileAsSource = async () => {
        if (!file) { toast.error("Bitte zuerst eine Datei wählen"); return; }
        setAutoBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/dealer/manufacturer-catalog/auto-import/file", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Datei als Wochen-Quelle hinterlegt (${data.codes_found} Codes) · Auto-Import aktiv`);
            await loadAuto();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Hinterlegen fehlgeschlagen");
        } finally { setAutoBusy(false); }
    };
    const runAutoNow = async () => {
        setAutoBusy(true);
        try {
            const { data } = await api.post("/dealer/manufacturer-catalog/auto-import/run-now");
            toast.success(`Auto-Import ausgeführt: ${data.codes_imported} Codes · ${data.vehicles_updated} Fahrzeuge aktualisiert`);
            await loadAuto();
            onImported?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Auto-Import fehlgeschlagen");
        } finally { setAutoBusy(false); }
    };

    const revertVehicle = async (v) => {
        try {
            await api.post(`/dealer/manufacturer-catalog/revert-vehicle/${v.id}`);
            setReverted((r) => ({ ...r, [v.id]: true }));
            toast.success(`„${v.title}" zurückgesetzt auf „${v.old_model || "—"}"`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
        }
    };

    const [revertingAll, setRevertingAll] = useState(false);
    const revertAll = async () => {
        const pending = (result?.updated_vehicles || []).filter((v) => !reverted[v.id]);
        if (!pending.length) return;
        setRevertingAll(true);
        let ok = 0;
        for (const v of pending) {
            try {
                await api.post(`/dealer/manufacturer-catalog/revert-vehicle/${v.id}`);
                setReverted((r) => ({ ...r, [v.id]: true }));
                ok += 1;
            } catch { /* einzelne Fehler ignorieren, Rest weiter zurücksetzen */ }
        }
        setRevertingAll(false);
        toast.success(`${ok} Fahrzeug(e) zurückgesetzt`);
    };

    const pickFile = async (f) => {
        setFile(f || null); setResult(null); setPreview(null);
        if (!f) return;
        setPreviewing(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/dealer/manufacturer-catalog/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data);
        } catch (e) {
            setPreview({ error: e?.response?.data?.detail || "Vorschau fehlgeschlagen", codes_found: 0 });
        } finally { setPreviewing(false); }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) pickFile(f);
    };

    const doImport = async () => {
        if (!file) { toast.error("Bitte eine Katalog-Datei wählen"); return; }
        setBusy(true); setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/dealer/manufacturer-catalog/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            toast.success(`${data.codes_imported} Bestellcodes importiert · ${data.vehicles_updated} Fahrzeuge aktualisiert`);
            onImported?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import fehlgeschlagen");
        } finally { setBusy(false); }
    };

    return (
        <>
            {!hideTrigger && (
                <Button type="button" variant="outline" onClick={() => setOpen(true)}
                    className="h-9 rounded-sm text-sm font-bold bg-white text-[#111827] hover:bg-gray-100" data-testid="open-catalog-import-btn">
                    <PackageSearch className="w-4 h-4 mr-1.5" /> Bestellcodes importieren
                </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="catalog-import-dialog">
                    <DialogHeader><DialogTitle>Hersteller-Bestellcodes importieren</DialogTitle></DialogHeader>
                    <DialogDescription className="sr-only">Katalog-Datei mit Materialcode und Modellbezeichnung importieren.</DialogDescription>
                    <p className="text-xs text-gray-500">
                        Datei mit Hersteller-Katalog (Materialcode → Modellbezeichnung). Vorhandene Fahrzeuge
                        mit passendem Bestellcode werden automatisch aktualisiert (Modell D.3).
                    </p>

                    {/* Drag & Drop-Zone — erkennt das Format automatisch (.txt, .csv, .tsv, .xlsx) */}
                    <label
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        className={`flex flex-col items-center justify-center gap-1.5 rounded-sm border-2 border-dashed px-4 py-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[#FFCC00] bg-amber-50" : "border-gray-300 bg-gray-50 hover:border-gray-400"}`}
                        data-testid="catalog-import-dropzone"
                    >
                        <Upload className="w-6 h-6 text-gray-400" />
                        {file ? (
                            <span className="text-sm font-bold text-gray-800 break-all" data-testid="catalog-import-filename">{file.name}</span>
                        ) : (
                            <>
                                <span className="text-sm font-bold text-gray-700">Datei hierher ziehen oder klicken</span>
                                <span className="text-[11px] text-gray-500">Format egal — .txt · .csv · .tsv · .xlsx (wird automatisch erkannt)</span>
                            </>
                        )}
                        <input type="file" accept=".txt,.csv,.tsv,.tab,.xlsx,.xlsm,text/plain"
                            onChange={(e) => pickFile(e.target.files?.[0] || null)}
                            className="hidden" data-testid="catalog-import-file" />
                    </label>

                    {/* Vorschau vor dem Übernehmen */}
                    {previewing && (
                        <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="catalog-preview-loading">
                            <Loader2 className="w-4 h-4 animate-spin" /> Datei wird analysiert…
                        </div>
                    )}
                    {preview && !previewing && !result && (
                        preview.error ? (
                            <div className="text-sm bg-red-50 border border-red-200 rounded-sm p-3 text-red-800" data-testid="catalog-preview-error">{preview.error}</div>
                        ) : (
                            <div className="text-sm bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-1.5" data-testid="catalog-preview">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                    <span><b data-testid="catalog-preview-count">{preview.codes_found}</b> Bestellcodes erkannt</span>
                                    <span className="text-[11px] text-gray-500">Format: {preview.detected_format}</span>
                                </div>
                                {preview.manufacturers?.length > 0 && (
                                    <div className="text-[11px] text-gray-500">Hersteller: {preview.manufacturers.join(", ")}</div>
                                )}
                                <div className="text-[11px] font-bold text-amber-800" data-testid="catalog-preview-vehicles">
                                    {preview.vehicles_to_update > 0
                                        ? `≈ ${preview.vehicles_to_update} Fahrzeug(e) werden aktualisiert${preview.vehicles_matched > preview.vehicles_to_update ? ` (von ${preview.vehicles_matched} mit passendem Bestellcode)` : ""}`
                                        : preview.vehicles_matched > 0
                                            ? `${preview.vehicles_matched} Fahrzeug(e) mit passendem Bestellcode — bereits aktuell, keine Änderung`
                                            : "Keine vorhandenen Fahrzeuge betroffen"}
                                </div>
                                {preview.sample?.length > 0 && (
                                    <div className="border border-gray-200 rounded-sm divide-y divide-gray-100 bg-white mt-1" data-testid="catalog-preview-sample">
                                        {preview.sample.map((s, i) => (
                                            <div key={i} className="px-2 py-1 flex items-center justify-between gap-2 text-xs">
                                                <span className="truncate"><b>{s.model}</b> <span className="text-gray-400">· {s.manufacturer || "—"}</span></span>
                                                <span className="shrink-0 font-mono text-[10px] text-gray-500">{s.order_code}{s.earliest_delivery ? ` · ${s.earliest_delivery}` : ""}</span>
                                            </div>
                                        ))}
                                        <div className="px-2 py-1 text-[10px] text-gray-400 italic">Vorschau der ersten {preview.sample.length} Zeilen — bitte prüfen, dann importieren.</div>
                                    </div>
                                )}
                            </div>
                        )
                    )}

                    {result ? (
                        <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-sm p-3 space-y-2" data-testid="catalog-import-result">
                            <div className="flex items-center gap-1.5 font-bold text-emerald-800"><CheckCircle2 className="w-4 h-4" /> Import erfolgreich</div>
                            <div className="text-emerald-900">{result.codes_imported} Bestellcodes · Hersteller: {result.manufacturers?.join(", ")}</div>
                            {result.detected_format && <div className="text-[11px] text-emerald-700">Erkanntes Format: {result.detected_format}</div>}
                            <div className="text-emerald-900 font-bold">{result.vehicles_updated} Fahrzeug(e) automatisch aktualisiert</div>
                            {result.updated_vehicles?.length > 0 && (
                                <>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[11px] text-emerald-700">Geänderte Fahrzeuge (alt → neu):</span>
                                        {result.updated_vehicles.some((v) => !reverted[v.id]) && (
                                            <button onClick={revertAll} disabled={revertingAll}
                                                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                                                data-testid="catalog-import-revert-all">
                                                {revertingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Alle zurücksetzen
                                            </button>
                                        )}
                                    </div>
                                    <div className="border border-emerald-200 rounded-sm bg-white divide-y divide-gray-100 max-h-56 overflow-y-auto" data-testid="catalog-import-updated-list">
                                    {result.updated_vehicles.map((v) => (
                                        <div key={v.id} className="px-2 py-1.5 flex items-center justify-between gap-2 text-xs" data-testid={`updated-vehicle-${v.id}`}>
                                            <div className="min-w-0">
                                                <div className="font-bold text-gray-800 truncate">{v.title} <span className="font-mono text-[10px] text-gray-400">· {v.order_code}</span></div>
                                                <div className="text-gray-500 truncate">
                                                    <span className="line-through">{v.old_model || "—"}</span> → <span className="text-emerald-700 font-bold">{v.new_model}</span>
                                                </div>
                                            </div>
                                            {reverted[v.id] ? (
                                                <span className="shrink-0 text-[10px] text-gray-400 italic">zurückgesetzt</span>
                                            ) : (
                                                <button onClick={() => revertVehicle(v)} className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 hover:text-amber-900 hover:underline" data-testid={`revert-vehicle-${v.id}`}>
                                                    <Undo2 className="w-3 h-3" /> Zurücksetzen
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <Button onClick={doImport} disabled={busy || previewing || !file || (preview && !preview.codes_found)}
                            className="rounded-sm font-bold bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827] w-full" data-testid="catalog-import-submit">
                            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                            {preview?.codes_found ? `${preview.codes_found} Bestellcodes importieren` : "Importieren"}
                        </Button>
                    )}

                    {/* Jul-2026 (User-Wunsch) — Auto-Import: montags automatisch aus hinterlegter Quelle */}
                    <div className="border border-gray-200 rounded-sm p-3 space-y-2 bg-gray-50" data-testid="catalog-autoimport-section">
                        <div className="flex items-center justify-between gap-2">
                            <div className="text-xs font-bold text-[#111827] flex items-center gap-1.5">
                                <RefreshCw className="w-3.5 h-3.5 text-[#b38f00]" /> Auto-Import (jeden Montag)
                            </div>
                            <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-bold">
                                <input type="checkbox" checked={!!auto?.enabled} disabled={autoBusy || auto === null}
                                    onChange={(e) => saveAuto({ enabled: e.target.checked })}
                                    className="w-4 h-4 accent-[#FFCC00]" data-testid="catalog-autoimport-toggle" />
                                aktiv
                            </label>
                        </div>
                        <p className="text-[11px] text-gray-500">
                            Hinterlege die Hersteller-Datei oder eine Download-URL — der Katalog wird dann
                            montags automatisch aktualisiert (inkl. Fahrzeug-Update), statt nur zu erinnern.
                        </p>
                        <div className="flex items-center gap-2">
                            <Input value={autoUrl} onChange={(e) => setAutoUrl(e.target.value)}
                                placeholder="https://… (Hersteller-Preisliste .csv/.txt/.xlsx)"
                                className="h-8 text-xs bg-white" data-testid="catalog-autoimport-url" />
                            <Button type="button" variant="outline" onClick={() => saveAuto({ source_url: autoUrl, enabled: true })}
                                disabled={autoBusy} className="h-8 text-xs rounded-sm shrink-0" data-testid="catalog-autoimport-save-url">
                                URL speichern
                            </Button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button type="button" variant="outline" onClick={storeFileAsSource} disabled={autoBusy || !file}
                                className="h-8 text-xs rounded-sm" data-testid="catalog-autoimport-store-file">
                                Gewählte Datei als Wochen-Quelle hinterlegen
                            </Button>
                            <Button type="button" onClick={runAutoNow} disabled={autoBusy || !auto?.enabled || (!auto?.has_file && !auto?.source_url)}
                                className="h-8 text-xs rounded-sm bg-[#111827] text-white hover:bg-black" data-testid="catalog-autoimport-run-now">
                                {autoBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Jetzt ausführen
                            </Button>
                        </div>
                        <div className="text-[11px] text-gray-500" data-testid="catalog-autoimport-status">
                            {auto?.has_file && <>Quelle: Datei <b>{auto.filename || "hinterlegt"}</b>{auto?.source_url ? " · " : ""}</>}
                            {auto?.source_url && <>URL-Quelle hinterlegt</>}
                            {!auto?.has_file && !auto?.source_url && <>Noch keine Quelle hinterlegt.</>}
                            {auto?.last_run_at && (
                                <> · Zuletzt: {new Date(auto.last_run_at).toLocaleString("de-DE")} — {auto?.last_result?.ok
                                    ? `✓ ${auto.last_result.codes_imported} Codes, ${auto.last_result.vehicles_updated} Fahrzeuge`
                                    : `✗ ${auto?.last_result?.error || "Fehler"}`}</>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export function CatalogOrderCodePicker({ onPick, withCounts = false }) {
    const [manufacturers, setManufacturers] = useState([]);
    const [manufacturer, setManufacturer] = useState("");
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasCatalog, setHasCatalog] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/dealer/manufacturer-catalog/manufacturers");
                setManufacturers(data.manufacturers || []);
                setHasCatalog((data.manufacturers || []).length > 0);
            } catch { setHasCatalog(false); }
        })();
    }, []);

    const search = useCallback(async () => {
        if (!manufacturer && !q) { setItems([]); return; }
        setLoading(true);
        try {
            const { data } = await api.get("/dealer/manufacturer-catalog", {
                params: { manufacturer, q, limit: 30, with_counts: withCounts },
            });
            setItems(data.items || []);
        } catch { setItems([]); } finally { setLoading(false); }
    }, [manufacturer, q, withCounts]);

    useEffect(() => {
        const t = setTimeout(search, 250);
        return () => clearTimeout(t);
    }, [search]);

    if (!hasCatalog) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-3 text-xs text-gray-500" data-testid="catalog-picker-empty">
                Noch kein Hersteller-Katalog importiert. In der Disposition über „Bestellcodes importieren" laden.
            </div>
        );
    }

    return (
        <div className="bg-white border border-amber-200 rounded-sm p-3 space-y-2" data-testid="catalog-order-code-picker">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-700">
                <PackageSearch className="w-4 h-4" /> Bestellcode aus Hersteller-Katalog wählen
            </div>
            <div className="flex flex-wrap gap-2">
                <Select value={manufacturer} onValueChange={setManufacturer}>
                    <SelectTrigger className="h-9 w-[180px] rounded-sm" data-testid="catalog-picker-manufacturer"><SelectValue placeholder="Hersteller" /></SelectTrigger>
                    <SelectContent>
                        {manufacturers.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Modell oder Bestellcode suchen…"
                    className="h-9 rounded-sm flex-1 min-w-[200px]" data-testid="catalog-picker-search" />
            </div>
            {loading ? (
                <div className="text-xs text-gray-400 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Suche…</div>
            ) : items.length > 0 ? (
                <div className="max-h-52 overflow-auto border border-gray-100 rounded-sm divide-y divide-gray-100" data-testid="catalog-picker-results">
                    {items.map((it) => (
                        <button key={it.order_code} type="button"
                            onClick={() => onPick?.(it)}
                            className="w-full text-left px-2.5 py-2 hover:bg-amber-50 text-sm flex items-center justify-between gap-2"
                            data-testid={`catalog-picker-item-${it.order_code}`}>
                            <span className="truncate flex items-center gap-1.5">
                                {withCounts && (
                                    <span className={`shrink-0 inline-flex items-center justify-center min-w-[2.2rem] px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${it.stock_count > 0 ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-400"}`}
                                        data-testid={`catalog-picker-count-${it.order_code}`} title="Fahrzeuge im Bestand mit diesem Bestellcode">
                                        {it.stock_count || 0}×
                                    </span>
                                )}
                                <span className="truncate"><b>{it.model}</b> <span className="text-gray-400">· {it.manufacturer}</span></span>
                            </span>
                            <span className="shrink-0 font-mono text-[11px] text-gray-500">{it.order_code}{it.earliest_delivery ? ` · ${it.earliest_delivery}` : ""}</span>
                        </button>
                    ))}
                </div>
            ) : (manufacturer || q) ? (
                <div className="text-xs text-gray-400" data-testid="catalog-picker-noresults">Keine Treffer.</div>
            ) : null}
        </div>
    );
}
